import type {
  AgeCategory,
  AssessmentDTO,
  AssessmentResponse,
  AuditLogDTO,
  AvailabilityRuleDTO,
  AvailabilityRuleResponse,
  BankAccountDTO,
  BookingDTO,
  ClientContactDTO,
  ClientContactResponse,
  CreateRelativeRequest,
  CreateRelativeResponse,
  GuardianshipDTO,
  GuardianshipResponse,
  UpdateGuardianshipRequest,
  CreateBankAccountRequest,
  CreateFeatureRequest,
  ClientDTO,
  ClientResponse,
  ClientTimeline,
  ClientTimelineResponse,
  CoachDTO,
  ConvertLeadResponse,
  CreateAssessmentRequest,
  CreateAvailabilityRuleRequest,
  CreateClientContactRequest,
  CreateClientRequest,
  CreateNoteRequest,
  CreateQuestionnaireRequest,
  CreateQuestionnaireResponse,
  CreateHtmlTemplateRequest,
  CreateQuestionnaireTemplateRequest,
  CreateReportRequest,
  CreateReportResponse,
  CreateRoleRequest,
  ChangeEmailRequest,
  ChangePasswordRequest,
  ChangePasswordResponse,
  CreateTeamMemberRequest,
  CreateTeamMemberResponse,
  DeliveryDTO,
  DeliveryResponse,
  DispatchQuestionnaireRequest,
  DraftHtmlPreviewResponse,
  DispatchQuestionnaireResponse,
  FeatureDTO,
  FingerImagePosition,
  FingerprintAnalysisDTO,
  FingerprintImageDTO,
  GenerateFingerprintReportRequest,
  GenerateFingerprintReportResponse,
  GenerateProfileResponse,
  GetFingerprintReportResponse,
  HtmlTemplateDTO,
  HtmlTemplateResponse,
  InvitePortalRequest,
  InvitePortalResponse,
  ListAllPackagesResponse,
  ListAssessmentsResponse,
  ListAuditLogsQuery,
  ListAvailabilityRulesResponse,
  ListBankAccountsResponse,
  ListReportSectionSettingsResponse,
  ListBookingsResponse,
  ListClientContactsResponse,
  ListGuardianshipsResponse,
  ListClientsQuery,
  ListCoachesResponse,
  ListFeaturesResponse,
  ListFingerprintAnalysesResponse,
  ListFingerprintImagesResponse,
  ListHtmlTemplatesResponse,
  ListLeadsQuery,
  ListNotesResponse,
  ListNotificationsResponse,
  ListPackagesResponse,
  ListQuestionnairesResponse,
  ListQuestionnaireTemplatesResponse,
  ListReportsQuery,
  ListRolesResponse,
  LoginRequest,
  MarkAllNotificationsReadResponse,
  MeResponse,
  NoteDTO,
  NoteResponse,
  NotificationResponse,
  Paginated,
  PackageDTO,
  PreviewHtmlTemplateRequest,
  QuestionnaireDTO,
  QuestionnaireResponse,
  QuestionnaireTemplateDTO,
  QuestionnaireTemplateResponse,
  RecordResponseRequest,
  RecordResponseResponse,
  RenderHtmlReportResponse,
  RenderPdfResponse,
  RenderPdfResult,
  ReportAnalyticsResponse,
  ReportContentOverrides,
  ReportDTO,
  ReportJobDTO,
  ReportJobResponse,
  ReportListItemDTO,
  ReportResponse,
  RescheduleBookingRequest,
  ResolveDmitInputRequest,
  ResolveDmitInputResponse,
  RoleDTO,
  LeadDTO,
  UpdateLeadStatusRequest,
  SendReportRequest,
  SendReportResponse,
  SendReportResult,
  SetReportVisibilityRequest,
  SetReportVisibilityResponse,
  SetPackageFeatureRequest,
  TokenPairResponse,
  UpdateAvailabilityRuleRequest,
  UpdateBankAccountRequest,
  UpdateClientContactRequest,
  ListPaymentSlipsResponse,
  ListPurchasesQuery,
  PurchaseDTO,
  RejectPurchaseRequest,
  ActivateResourceVersionResponse,
  ApproveResourceResponse,
  CreateResourceGroupRequest,
  CustomizeResourceRequest,
  CustomizeResourceResponse,
  GetLatestResourceCustomizationJobResponse,
  GetResourceCustomizationJobResponse,
  GetResourceTranslationJobResponse,
  ListResourceGroupsResponse,
  ListResourceVersionsResponse,
  ResourceCustomizationJobDTO,
  ResourceDTO,
  ResourceGroupDTO,
  ResourceGroupResponse,
  ResourceTranslationJobDTO,
  SendResourceGroupRequest,
  SendResourceGroupResponse,
  SendResourceRequest,
  SendResourceResponse,
  SetGroupSendSelectionRequest,
  SetGroupSendSelectionResponse,
  TranslateResourceRequest,
  TranslateResourceResponse,
  UpdateClientRequest,
  UpdateResourceGroupRequest,
  UpdateResourceRequest,
  UpdateFeatureRequest,
  UpdateHtmlTemplateRequest,
  UpdatePackageRequest,
  UpdateQuestionnaireTemplateRequest,
  UpdateRoleRequest,
  UpdateProfileRequest,
  UpdateProfileResponse,
  UpdateReportSectionSettingRequest,
  UpdateReportSectionSettingResponse,
  UpdateTeamMemberRequest,
  UploadFingerprintAnalysisResponse,
  UploadFingerprintImageResponse,
  VerifyEmailChangeResponse,
} from '@brainkey/shared';

import { API_BASE_URL, API_PREFIX } from '../config';

import { ApiError, apiRequest } from './client';
import { tokenStore } from './tokenStore';

// ── Auth ────────────────────────────────────────────────────────────

export const authApi = {
  login(body: LoginRequest): Promise<TokenPairResponse> {
    return apiRequest<TokenPairResponse>('/auth/login', {
      method: 'POST',
      body,
      skipAuth: true,
    });
  },

  logout(refreshToken: string): Promise<void> {
    return apiRequest<void>('/auth/logout', {
      method: 'POST',
      body: { refreshToken },
      skipAuth: true,
    });
  },

  me(signal?: AbortSignal): Promise<MeResponse> {
    return apiRequest<MeResponse>('/auth/me', signal ? { signal } : {});
  },

  changePassword(body: ChangePasswordRequest): Promise<ChangePasswordResponse> {
    return apiRequest<ChangePasswordResponse>('/auth/change-password', { method: 'POST', body });
  },

  changeEmail(body: ChangeEmailRequest): Promise<void> {
    return apiRequest<void>('/auth/change-email', { method: 'POST', body });
  },

  /** `skipAuth: true` — the coach may click the confirmation link while
   * logged out or on a different device. */
  verifyEmailChange(token: string): Promise<VerifyEmailChangeResponse> {
    return apiRequest<VerifyEmailChangeResponse>('/auth/verify-email-change', {
      method: 'POST',
      body: { token },
      skipAuth: true,
    });
  },

  async updateProfile(body: UpdateProfileRequest): Promise<CoachDTO> {
    const res = await apiRequest<UpdateProfileResponse>('/auth/me', { method: 'PATCH', body });
    return res.coach;
  },
};

// ── Clients ─────────────────────────────────────────────────────────

export const clientsApi = {
  list(query: ListClientsQuery = {}, signal?: AbortSignal): Promise<Paginated<ClientDTO>> {
    return apiRequest<Paginated<ClientDTO>>('/clients', {
      query: {
        q: query.q,
        languagePref: query.languagePref,
        ageCategory: query.ageCategory,
        clientType: query.clientType,
        coachId: query.coachId,
        includeDeleted: query.includeDeleted,
        page: query.page,
        pageSize: query.pageSize,
      },
      ...(signal ? { signal } : {}),
    });
  },

  async get(id: string, signal?: AbortSignal): Promise<ClientDTO> {
    const res = await apiRequest<ClientResponse>(`/clients/${id}`, signal ? { signal } : {});
    return res.client;
  },

  async create(body: CreateClientRequest): Promise<ClientDTO> {
    const res = await apiRequest<ClientResponse>('/clients', { method: 'POST', body });
    return res.client;
  },

  async update(id: string, body: UpdateClientRequest): Promise<ClientDTO> {
    const res = await apiRequest<ClientResponse>(`/clients/${id}`, { method: 'PATCH', body });
    return res.client;
  },

  remove(id: string): Promise<void> {
    return apiRequest<void>(`/clients/${id}`, { method: 'DELETE' });
  },

  async timeline(id: string, signal?: AbortSignal): Promise<ClientTimeline> {
    const res = await apiRequest<ClientTimelineResponse>(
      `/clients/${id}/timeline`,
      signal ? { signal } : {},
    );
    return res.timeline;
  },

  invitePortal(id: string, body: InvitePortalRequest = {}): Promise<InvitePortalResponse> {
    return apiRequest<InvitePortalResponse>(`/clients/${id}/portal-invite`, {
      method: 'POST',
      body,
    });
  },
};

// ── Assessments ───────────────────────────────────────────────────────

export const assessmentsApi = {
  async create(clientId: string, body: CreateAssessmentRequest): Promise<AssessmentDTO> {
    const res = await apiRequest<AssessmentResponse>(`/clients/${clientId}/assessments`, {
      method: 'POST',
      body,
    });
    return res.assessment;
  },

  async listForClient(clientId: string, signal?: AbortSignal): Promise<AssessmentDTO[]> {
    const res = await apiRequest<ListAssessmentsResponse>(
      `/clients/${clientId}/assessments`,
      signal ? { signal } : {},
    );
    return res.assessments;
  },

  /** Assemble a DMIT profile input via AUTO/MANUAL/DEFAULT (RE-P2-T02) —
   * stateless, no assessment created yet. */
  resolveInput(clientId: string, body: ResolveDmitInputRequest): Promise<ResolveDmitInputResponse> {
    return apiRequest<ResolveDmitInputResponse>(`/clients/${clientId}/assessments/input/resolve`, {
      method: 'POST',
      body,
    });
  },

  async getById(id: string, signal?: AbortSignal): Promise<AssessmentDTO> {
    const res = await apiRequest<AssessmentResponse>(
      `/assessments/${id}`,
      signal ? { signal } : {},
    );
    return res.assessment;
  },
};

// ── Intelligence Profiles ─────────────────────────────────────────────

export const profilesApi = {
  /**
   * Generate-or-return the deterministic intelligence profile for an
   * assessment (GENERATE ONCE — safe to call repeatedly, returns the cached
   * row after the first call).
   */
  generate(assessmentId: string): Promise<GenerateProfileResponse> {
    return apiRequest<GenerateProfileResponse>(`/assessments/${assessmentId}/profile`, {
      method: 'POST',
    });
  },
};

// ── Questionnaires ───────────────────────────────────────────────────

export const questionnairesApi = {
  create(clientId: string, body: CreateQuestionnaireRequest): Promise<CreateQuestionnaireResponse> {
    return apiRequest<CreateQuestionnaireResponse>(`/clients/${clientId}/questionnaires`, {
      method: 'POST',
      body,
    });
  },

  async listForClient(clientId: string, signal?: AbortSignal): Promise<QuestionnaireDTO[]> {
    const res = await apiRequest<ListQuestionnairesResponse>(
      `/clients/${clientId}/questionnaires`,
      signal ? { signal } : {},
    );
    return res.questionnaires;
  },

  async get(id: string, signal?: AbortSignal): Promise<QuestionnaireDTO> {
    const res = await apiRequest<QuestionnaireResponse>(
      `/questionnaires/${id}`,
      signal ? { signal } : {},
    );
    return res.questionnaire;
  },

  dispatch(id: string, body: DispatchQuestionnaireRequest): Promise<DispatchQuestionnaireResponse> {
    return apiRequest<DispatchQuestionnaireResponse>(`/questionnaires/${id}/dispatch`, {
      method: 'POST',
      body,
    });
  },

  resend(id: string, body: DispatchQuestionnaireRequest): Promise<DispatchQuestionnaireResponse> {
    return apiRequest<DispatchQuestionnaireResponse>(`/questionnaires/${id}/resend`, {
      method: 'POST',
      body,
    });
  },

  async recordResponse(id: string, body: RecordResponseRequest): Promise<QuestionnaireDTO> {
    const res = await apiRequest<RecordResponseResponse>(`/questionnaires/${id}/response`, {
      method: 'POST',
      body,
    });
    return res.questionnaire;
  },
};

// ── Delivery ──────────────────────────────────────────────────────────

export const deliveryApi = {
  async sendReport(reportId: string, body: SendReportRequest): Promise<SendReportResult> {
    const res = await apiRequest<SendReportResponse>(`/reports/${reportId}/send`, {
      method: 'POST',
      body,
    });
    return res.delivery;
  },

  async resendReport(reportId: string, body: SendReportRequest): Promise<SendReportResult> {
    const res = await apiRequest<SendReportResponse>(`/reports/${reportId}/resend`, {
      method: 'POST',
      body,
    });
    return res.delivery;
  },

  async get(id: string, signal?: AbortSignal): Promise<DeliveryDTO> {
    const res = await apiRequest<DeliveryResponse>(`/deliveries/${id}`, signal ? { signal } : {});
    return res.delivery;
  },
};

// ── Reports / Report Studio ──────────────────────────────────────────

export const reportsApi = {
  list(query: ListReportsQuery = {}, signal?: AbortSignal): Promise<Paginated<ReportListItemDTO>> {
    return apiRequest<Paginated<ReportListItemDTO>>('/reports', {
      query: {
        q: query.q,
        status: query.status,
        variant: query.variant,
        language: query.language,
        clientId: query.clientId,
        reportType: query.reportType,
        sent: query.sent,
        page: query.page,
        pageSize: query.pageSize,
      },
      ...(signal ? { signal } : {}),
    });
  },

  async get(id: string, signal?: AbortSignal): Promise<ReportDTO> {
    const res = await apiRequest<ReportResponse>(`/reports/${id}`, signal ? { signal } : {});
    return res.report;
  },

  trigger(assessmentId: string, body: CreateReportRequest): Promise<CreateReportResponse> {
    return apiRequest<CreateReportResponse>(`/assessments/${assessmentId}/reports`, {
      method: 'POST',
      body,
    });
  },

  async getJob(jobId: string, signal?: AbortSignal): Promise<ReportJobDTO> {
    const res = await apiRequest<ReportJobResponse>(
      `/reports/jobs/${jobId}`,
      signal ? { signal } : {},
    );
    return res.job;
  },

  analytics(signal?: AbortSignal): Promise<ReportAnalyticsResponse> {
    return apiRequest<ReportAnalyticsResponse>('/reports/analytics', signal ? { signal } : {});
  },

  async renderPdf(reportId: string): Promise<RenderPdfResult> {
    const res = await apiRequest<RenderPdfResponse>(`/reports/${reportId}/pdf`, {
      method: 'POST',
    });
    return res.pdf;
  },

  async getPdf(reportId: string, signal?: AbortSignal): Promise<RenderPdfResult | null> {
    try {
      const res = await apiRequest<RenderPdfResponse>(
        `/reports/${reportId}/pdf`,
        signal ? { signal } : {},
      );
      return res.pdf;
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return null;
      throw err;
    }
  },

  /** Coach edit layer (reports:edit, Brain Skills only) — full-document
   * replace of the report's content overrides. */
  async saveContentOverrides(
    reportId: string,
    overrides: ReportContentOverrides,
  ): Promise<ReportDTO> {
    const res = await apiRequest<ReportResponse>(`/reports/${reportId}/content-overrides`, {
      method: 'PATCH',
      body: { overrides },
    });
    return res.report;
  },

  /** Edit Report live preview: compiles the document with DRAFT overrides
   * server-side (never persists). */
  draftHtmlPreview(
    reportId: string,
    overrides: ReportContentOverrides,
  ): Promise<DraftHtmlPreviewResponse> {
    return apiRequest<DraftHtmlPreviewResponse>(`/reports/${reportId}/html-preview`, {
      method: 'POST',
      body: { overrides },
    });
  },

  async setVisibility(reportId: string, body: SetReportVisibilityRequest): Promise<ReportDTO> {
    const res = await apiRequest<SetReportVisibilityResponse>(`/reports/${reportId}/visibility`, {
      method: 'PATCH',
      body,
    });
    return res.report;
  },

  remove(reportId: string): Promise<void> {
    return apiRequest<void>(`/reports/${reportId}`, { method: 'DELETE' });
  },
};

// ── Questionnaire Templates (DEC-0049) ───────────────────────────────

export const questionnaireTemplatesApi = {
  async list(signal?: AbortSignal): Promise<QuestionnaireTemplateDTO[]> {
    const res = await apiRequest<ListQuestionnaireTemplatesResponse>(
      '/questionnaire-templates',
      signal ? { signal } : {},
    );
    return res.templates;
  },

  async getActive(signal?: AbortSignal): Promise<QuestionnaireTemplateDTO | null> {
    try {
      const res = await apiRequest<QuestionnaireTemplateResponse>(
        '/questionnaire-templates/active',
        signal ? { signal } : {},
      );
      return res.template;
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return null;
      throw err;
    }
  },

  async getById(id: string, signal?: AbortSignal): Promise<QuestionnaireTemplateDTO> {
    const res = await apiRequest<QuestionnaireTemplateResponse>(
      `/questionnaire-templates/${id}`,
      signal ? { signal } : {},
    );
    return res.template;
  },

  async create(body: CreateQuestionnaireTemplateRequest): Promise<QuestionnaireTemplateDTO> {
    const res = await apiRequest<QuestionnaireTemplateResponse>('/questionnaire-templates', {
      method: 'POST',
      body,
    });
    return res.template;
  },

  async update(
    id: string,
    body: UpdateQuestionnaireTemplateRequest,
  ): Promise<QuestionnaireTemplateDTO> {
    const res = await apiRequest<QuestionnaireTemplateResponse>(`/questionnaire-templates/${id}`, {
      method: 'PATCH',
      body,
    });
    return res.template;
  },

  async activate(id: string): Promise<QuestionnaireTemplateDTO> {
    const res = await apiRequest<QuestionnaireTemplateResponse>(
      `/questionnaire-templates/${id}/activate`,
      { method: 'POST' },
    );
    return res.template;
  },
};

// ── HTML Report Templates (visual builder) ───────────────────────────

export const htmlTemplatesApi = {
  async list(category?: string, signal?: AbortSignal): Promise<HtmlTemplateDTO[]> {
    const res = await apiRequest<ListHtmlTemplatesResponse>('/html-templates', {
      query: { category },
      ...(signal ? { signal } : {}),
    });
    return res.templates;
  },

  async getActive(category: string, signal?: AbortSignal): Promise<HtmlTemplateDTO | null> {
    try {
      const res = await apiRequest<HtmlTemplateResponse>('/html-templates/active', {
        query: { category },
        ...(signal ? { signal } : {}),
      });
      return res.template;
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return null;
      throw err;
    }
  },

  async getById(id: string, signal?: AbortSignal): Promise<HtmlTemplateDTO> {
    const res = await apiRequest<HtmlTemplateResponse>(
      `/html-templates/${id}`,
      signal ? { signal } : {},
    );
    return res.template;
  },

  async create(body: CreateHtmlTemplateRequest): Promise<HtmlTemplateDTO> {
    const res = await apiRequest<HtmlTemplateResponse>('/html-templates', {
      method: 'POST',
      body,
    });
    return res.template;
  },

  async update(id: string, body: UpdateHtmlTemplateRequest): Promise<HtmlTemplateDTO> {
    const res = await apiRequest<HtmlTemplateResponse>(`/html-templates/${id}`, {
      method: 'PATCH',
      body,
    });
    return res.template;
  },

  async activate(id: string): Promise<HtmlTemplateDTO> {
    const res = await apiRequest<HtmlTemplateResponse>(`/html-templates/${id}/activate`, {
      method: 'POST',
    });
    return res.template;
  },

  preview(body: PreviewHtmlTemplateRequest): Promise<RenderHtmlReportResponse> {
    return apiRequest<RenderHtmlReportResponse>('/html-templates/preview', {
      method: 'POST',
      body,
    });
  },
};

// ── Notes ─────────────────────────────────────────────────────────────

export const notesApi = {
  async create(clientId: string, body: CreateNoteRequest): Promise<NoteDTO> {
    const res = await apiRequest<NoteResponse>(`/clients/${clientId}/notes`, {
      method: 'POST',
      body,
    });
    return res.note;
  },

  async listForClient(clientId: string, signal?: AbortSignal): Promise<NoteDTO[]> {
    const res = await apiRequest<ListNotesResponse>(
      `/clients/${clientId}/notes`,
      signal ? { signal } : {},
    );
    return res.notes;
  },
};

// ── Brain Skills Analysis (fingerprint uploads, DEC-0052) ────────────
// A standalone module — unrelated to assessmentsApi/the DMIT AUTO/MANUAL/
// DEFAULT flow.

export const fingerprintAnalysisApi = {
  async upload(clientId: string, file: File): Promise<FingerprintAnalysisDTO> {
    const formData = new FormData();
    formData.append('file', file);
    const res = await apiRequest<UploadFingerprintAnalysisResponse>(
      `/clients/${clientId}/fingerprint-analyses`,
      { method: 'POST', body: formData },
    );
    return res.fingerprintAnalysis;
  },

  async listForClient(clientId: string, signal?: AbortSignal): Promise<FingerprintAnalysisDTO[]> {
    const res = await apiRequest<ListFingerprintAnalysesResponse>(
      `/clients/${clientId}/fingerprint-analyses`,
      signal ? { signal } : {},
    );
    return res.fingerprintAnalyses;
  },

  /** Generates (or re-uses, GENERATE ONCE) the Brain Skills report — a real
   * `Report` row rendered through the same Report/HtmlReportTemplate/
   * Playwright pipeline as every other report (P13-T02/DEC-0058). */
  async generateReport(
    clientId: string,
    analysisId: string,
    body: GenerateFingerprintReportRequest = {},
  ): Promise<ReportDTO> {
    const res = await apiRequest<GenerateFingerprintReportResponse>(
      `/clients/${clientId}/fingerprint-analyses/${analysisId}/report`,
      { method: 'POST', body },
    );
    return res.report;
  },

  /** True force-regenerate (bug fix): deletes the existing report and
   * creates a fresh one against the current active template/content logic
   * — unlike `generateReport` above, which is a GENERATE-ONCE no-op once a
   * report already exists. Destructive: the deleted report's delivery/
   * notification history is lost — callers must confirm with the coach
   * before calling this. */
  async regenerateReport(clientId: string, analysisId: string): Promise<ReportDTO> {
    const res = await apiRequest<GenerateFingerprintReportResponse>(
      `/clients/${clientId}/fingerprint-analyses/${analysisId}/report/regenerate`,
      { method: 'POST' },
    );
    return res.report;
  },

  /** `null` if no report has been generated yet (404 tolerant). */
  async getReport(clientId: string, analysisId: string): Promise<ReportDTO | null> {
    try {
      const res = await apiRequest<GetFingerprintReportResponse>(
        `/clients/${clientId}/fingerprint-analyses/${analysisId}/report`,
      );
      return res.report;
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return null;
      throw err;
    }
  },

  /** Top-level counterpart (P13-T02/DEC-0058) — the unified Reports page's
   * "generate from an uploaded analysis" flow, which doesn't have a
   * client-scoped URL in context at trigger time. Same underlying
   * generation as `generateReport` above. */
  async generateReportFromAnalysis(
    analysisId: string,
    body: GenerateFingerprintReportRequest = {},
  ): Promise<ReportDTO> {
    const res = await apiRequest<GenerateFingerprintReportResponse>(
      `/fingerprint-analyses/${analysisId}/reports`,
      { method: 'POST', body },
    );
    return res.report;
  },

  /** Permanent delete (P22-T04) — the API 409s if a report was already
   * generated from this workbook; the coach must delete that report first. */
  remove(clientId: string, analysisId: string): Promise<void> {
    return apiRequest<void>(`/clients/${clientId}/fingerprint-analyses/${analysisId}`, {
      method: 'DELETE',
    });
  },
};

// ── Client Relation Contacts (Family & Relations, DEC-0042) ─────────

export const clientContactsApi = {
  async create(clientId: string, body: CreateClientContactRequest): Promise<ClientContactDTO> {
    const res = await apiRequest<ClientContactResponse>(`/clients/${clientId}/contacts`, {
      method: 'POST',
      body,
    });
    return res.contact;
  },

  async listForClient(clientId: string, signal?: AbortSignal): Promise<ClientContactDTO[]> {
    const res = await apiRequest<ListClientContactsResponse>(
      `/clients/${clientId}/contacts`,
      signal ? { signal } : {},
    );
    return res.contacts;
  },

  async update(
    clientId: string,
    id: string,
    body: UpdateClientContactRequest,
  ): Promise<ClientContactDTO> {
    const res = await apiRequest<ClientContactResponse>(`/clients/${clientId}/contacts/${id}`, {
      method: 'PATCH',
      body,
    });
    return res.contact;
  },

  remove(clientId: string, id: string): Promise<void> {
    return apiRequest<void>(`/clients/${clientId}/contacts/${id}`, { method: 'DELETE' });
  },
};

// ── Household / relatives (account-less profiles under a parent's L-number) ──

export const guardianshipsApi = {
  list(clientId: string, signal?: AbortSignal): Promise<ListGuardianshipsResponse> {
    return apiRequest<ListGuardianshipsResponse>(
      `/clients/${clientId}/guardianships`,
      signal ? { signal } : {},
    );
  },

  async createRelative(clientId: string, body: CreateRelativeRequest): Promise<GuardianshipDTO> {
    const res = await apiRequest<CreateRelativeResponse>(`/clients/${clientId}/relatives`, {
      method: 'POST',
      body,
    });
    return res.guardianship;
  },

  async update(
    clientId: string,
    linkId: string,
    body: UpdateGuardianshipRequest,
  ): Promise<GuardianshipDTO> {
    const res = await apiRequest<GuardianshipResponse>(
      `/clients/${clientId}/guardianships/${linkId}`,
      { method: 'PATCH', body },
    );
    return res.guardianship;
  },

  remove(clientId: string, linkId: string): Promise<void> {
    return apiRequest<void>(`/clients/${clientId}/guardianships/${linkId}`, { method: 'DELETE' });
  },
};

// ── Client Session Booking (CP-P4-T01/DEC-0037) ──────────────────────

export const availabilityApi = {
  async list(signal?: AbortSignal): Promise<AvailabilityRuleDTO[]> {
    const res = await apiRequest<ListAvailabilityRulesResponse>(
      '/coach/availability',
      signal ? { signal } : {},
    );
    return res.rules;
  },

  async create(body: CreateAvailabilityRuleRequest): Promise<AvailabilityRuleDTO> {
    const res = await apiRequest<AvailabilityRuleResponse>('/coach/availability', {
      method: 'POST',
      body,
    });
    return res.rule;
  },

  async update(id: string, body: UpdateAvailabilityRuleRequest): Promise<AvailabilityRuleDTO> {
    const res = await apiRequest<AvailabilityRuleResponse>(`/coach/availability/${id}`, {
      method: 'PATCH',
      body,
    });
    return res.rule;
  },

  remove(id: string): Promise<void> {
    return apiRequest<void>(`/coach/availability/${id}`, { method: 'DELETE' });
  },
};

export const bookingsApi = {
  async listForCoach(signal?: AbortSignal): Promise<BookingDTO[]> {
    const res = await apiRequest<ListBookingsResponse>('/coach/bookings', signal ? { signal } : {});
    return res.bookings;
  },

  async listForCoachInRange(from: string, to: string, signal?: AbortSignal): Promise<BookingDTO[]> {
    const query = new URLSearchParams({ from, to }).toString();
    const res = await apiRequest<ListBookingsResponse>(
      `/coach/bookings?${query}`,
      signal ? { signal } : {},
    );
    return res.bookings;
  },

  async listForClient(clientId: string, signal?: AbortSignal): Promise<BookingDTO[]> {
    const res = await apiRequest<ListBookingsResponse>(
      `/clients/${clientId}/bookings`,
      signal ? { signal } : {},
    );
    return res.bookings;
  },

  async cancel(id: string): Promise<BookingDTO> {
    const res = await apiRequest<{ booking: BookingDTO }>(`/coach/bookings/${id}/cancel`, {
      method: 'PATCH',
    });
    return res.booking;
  },

  async reschedule(id: string, body: RescheduleBookingRequest): Promise<BookingDTO> {
    const res = await apiRequest<{ booking: BookingDTO }>(`/coach/bookings/${id}/reschedule`, {
      method: 'PATCH',
      body,
    });
    return res.booking;
  },
};

// ── Notifications ────────────────────────────────────────────────────

export const notificationsApi = {
  list(signal?: AbortSignal): Promise<ListNotificationsResponse> {
    return apiRequest<ListNotificationsResponse>('/coach/notifications', signal ? { signal } : {});
  },

  async markRead(id: string): Promise<NotificationResponse['notification']> {
    const res = await apiRequest<NotificationResponse>(`/coach/notifications/${id}/read`, {
      method: 'PATCH',
    });
    return res.notification;
  },

  markAllRead(): Promise<MarkAllNotificationsReadResponse> {
    return apiRequest<MarkAllNotificationsReadResponse>('/coach/notifications/read-all', {
      method: 'PATCH',
    });
  },
};

// ── Roles & Permissions (RBAC) ───────────────────────────────────────

export const rolesApi = {
  async list(signal?: AbortSignal): Promise<RoleDTO[]> {
    const res = await apiRequest<ListRolesResponse>('/roles', signal ? { signal } : {});
    return res.roles;
  },

  async create(body: CreateRoleRequest): Promise<RoleDTO> {
    const res = await apiRequest<{ role: RoleDTO }>('/roles', { method: 'POST', body });
    return res.role;
  },

  async update(id: string, body: UpdateRoleRequest): Promise<RoleDTO> {
    const res = await apiRequest<{ role: RoleDTO }>(`/roles/${id}`, { method: 'PATCH', body });
    return res.role;
  },

  remove(id: string): Promise<void> {
    return apiRequest<void>(`/roles/${id}`, { method: 'DELETE' });
  },
};

// ── Team (staff account management) ──────────────────────────────────

export const teamApi = {
  async list(signal?: AbortSignal): Promise<CoachDTO[]> {
    const res = await apiRequest<ListCoachesResponse>('/coaches', signal ? { signal } : {});
    return res.coaches;
  },

  create(body: CreateTeamMemberRequest): Promise<CreateTeamMemberResponse> {
    return apiRequest<CreateTeamMemberResponse>('/coaches', { method: 'POST', body });
  },

  async update(id: string, body: UpdateTeamMemberRequest): Promise<CoachDTO> {
    const res = await apiRequest<{ coach: CoachDTO }>(`/coaches/${id}`, {
      method: 'PATCH',
      body,
    });
    return res.coach;
  },
};

// ── Audit Log (Owner-only "who did what" accountability trail) ──────

export const auditLogApi = {
  list(query: ListAuditLogsQuery = {}, signal?: AbortSignal): Promise<Paginated<AuditLogDTO>> {
    return apiRequest<Paginated<AuditLogDTO>>('/audit-logs', {
      query: {
        actorId: query.actorId,
        entityType: query.entityType,
        from: query.from,
        to: query.to,
        page: query.page,
        pageSize: query.pageSize,
      },
      ...(signal ? { signal } : {}),
    });
  },
};

// ── Leads (public "Contact Us" submissions, P21-T03) ─────────────────

export const leadsApi = {
  list(query: ListLeadsQuery = {}, signal?: AbortSignal): Promise<Paginated<LeadDTO>> {
    return apiRequest<Paginated<LeadDTO>>('/leads', {
      query: { status: query.status, page: query.page, pageSize: query.pageSize },
      ...(signal ? { signal } : {}),
    });
  },

  async get(id: string, signal?: AbortSignal): Promise<LeadDTO> {
    const res = await apiRequest<{ lead: LeadDTO }>(`/leads/${id}`, signal ? { signal } : {});
    return res.lead;
  },

  async setStatus(id: string, body: UpdateLeadStatusRequest): Promise<LeadDTO> {
    const res = await apiRequest<{ lead: LeadDTO }>(`/leads/${id}/status`, {
      method: 'PATCH',
      body,
    });
    return res.lead;
  },

  /** Converts a lead into a real Client via the same `ClientService.create`
   * path as `clientsApi.create` — the lead is marked CONVERTED atomically
   * server-side. */
  convert(id: string, body: CreateClientRequest): Promise<ConvertLeadResponse> {
    return apiRequest<ConvertLeadResponse>(`/leads/${id}/convert`, { method: 'POST', body });
  },
};

// ── Packages (public read-only catalog, P21-T01) ─────────────────────

export const packagesApi = {
  async list(signal?: AbortSignal): Promise<PackageDTO[]> {
    const res = await apiRequest<ListPackagesResponse>('/packages', signal ? { signal } : {});
    return res.packages;
  },

  // ── Admin (Settings > Packages, requires settings:workspace-manage) ──

  async listAll(signal?: AbortSignal): Promise<PackageDTO[]> {
    const res = await apiRequest<ListAllPackagesResponse>(
      '/packages/all',
      signal ? { signal } : {},
    );
    return res.packages;
  },

  async update(id: string, body: UpdatePackageRequest): Promise<PackageDTO> {
    const res = await apiRequest<{ package: PackageDTO }>(`/packages/${id}`, {
      method: 'PATCH',
      body,
    });
    return res.package;
  },

  async setFeature(packageId: string, body: SetPackageFeatureRequest): Promise<PackageDTO> {
    const res = await apiRequest<{ package: PackageDTO }>(`/packages/${packageId}/features`, {
      method: 'POST',
      body,
    });
    return res.package;
  },

  async removeFeature(packageId: string, featureId: string): Promise<PackageDTO> {
    const res = await apiRequest<{ package: PackageDTO }>(
      `/packages/${packageId}/features/${featureId}`,
      { method: 'DELETE' },
    );
    return res.package;
  },
};

// ── Feature catalog (Settings > Packages, requires settings:workspace-manage) ──

export const featuresApi = {
  async list(signal?: AbortSignal): Promise<FeatureDTO[]> {
    const res = await apiRequest<ListFeaturesResponse>('/features', signal ? { signal } : {});
    return res.features;
  },

  async create(body: CreateFeatureRequest): Promise<FeatureDTO> {
    const res = await apiRequest<{ feature: FeatureDTO }>('/features', { method: 'POST', body });
    return res.feature;
  },

  async update(id: string, body: UpdateFeatureRequest): Promise<FeatureDTO> {
    const res = await apiRequest<{ feature: FeatureDTO }>(`/features/${id}`, {
      method: 'PATCH',
      body,
    });
    return res.feature;
  },

  remove(id: string): Promise<void> {
    return apiRequest<void>(`/features/${id}`, { method: 'DELETE' });
  },
};

// ── Bank accounts (Settings, requires settings:workspace-manage) ──────

export const bankAccountsApi = {
  async listAll(signal?: AbortSignal): Promise<BankAccountDTO[]> {
    const res = await apiRequest<ListBankAccountsResponse>(
      '/bank-accounts/all',
      signal ? { signal } : {},
    );
    return res.bankAccounts;
  },

  async create(body: CreateBankAccountRequest): Promise<BankAccountDTO> {
    const res = await apiRequest<{ bankAccount: BankAccountDTO }>('/bank-accounts', {
      method: 'POST',
      body,
    });
    return res.bankAccount;
  },

  async update(id: string, body: UpdateBankAccountRequest): Promise<BankAccountDTO> {
    const res = await apiRequest<{ bankAccount: BankAccountDTO }>(`/bank-accounts/${id}`, {
      method: 'PATCH',
      body,
    });
    return res.bankAccount;
  },

  remove(id: string): Promise<void> {
    return apiRequest<void>(`/bank-accounts/${id}`, { method: 'DELETE' });
  },
};

// ── Report sections per age group (Settings, requires settings:workspace-manage) ──

export const reportSectionSettingsApi = {
  async list(signal?: AbortSignal): Promise<ListReportSectionSettingsResponse['settings']> {
    const res = await apiRequest<ListReportSectionSettingsResponse>(
      '/report-section-settings',
      signal ? { signal } : {},
    );
    return res.settings;
  },

  async update(
    ageCategory: AgeCategory,
    body: UpdateReportSectionSettingRequest,
  ): Promise<UpdateReportSectionSettingResponse['setting']> {
    const res = await apiRequest<UpdateReportSectionSettingResponse>(
      `/report-section-settings/${ageCategory}`,
      { method: 'PUT', body },
    );
    return res.setting;
  },
};

// ── Resources (coach-supplied PDF library, P23) ───────────────────────
// Never AI-generated — a title/description/language catalog the coach
// uploads PDFs into, then sends to clients via the existing WhatsApp/email
// delivery pipeline (see `useSendResource`).

export const resourcesApi = {
  async listAll(signal?: AbortSignal): Promise<ResourceDTO[]> {
    const res = await apiRequest<{ resources: ResourceDTO[] }>(
      '/resources',
      signal ? { signal } : {},
    );
    return res.resources;
  },

  async create(
    fields: { title: string; description?: string; language: string; groupId?: string },
    file: File,
  ): Promise<ResourceDTO> {
    const formData = new FormData();
    formData.append('title', fields.title);
    if (fields.description !== undefined) formData.append('description', fields.description);
    formData.append('language', fields.language);
    if (fields.groupId !== undefined) formData.append('groupId', fields.groupId);
    formData.append('file', file);
    const res = await apiRequest<{ resource: ResourceDTO }>('/resources', {
      method: 'POST',
      body: formData,
    });
    return res.resource;
  },

  async update(id: string, fields: UpdateResourceRequest, file?: File): Promise<ResourceDTO> {
    const formData = new FormData();
    if (fields.title !== undefined) formData.append('title', fields.title);
    if (fields.description !== undefined) formData.append('description', fields.description);
    // '' clears the group — see UpdateResourceRequest's doc comment.
    if (fields.groupId !== undefined) formData.append('groupId', fields.groupId);
    if (file) formData.append('file', file);
    const res = await apiRequest<{ resource: ResourceDTO }>(`/resources/${id}`, {
      method: 'PATCH',
      body: formData,
    });
    return res.resource;
  },

  remove(id: string): Promise<void> {
    return apiRequest<void>(`/resources/${id}`, { method: 'DELETE' });
  },

  async send(
    clientId: string,
    resourceId: string,
    body: SendResourceRequest,
  ): Promise<SendResourceResponse['delivery']> {
    const res = await apiRequest<SendResourceResponse>(
      `/clients/${clientId}/resources/${resourceId}/send`,
      { method: 'POST', body },
    );
    return res.delivery;
  },

  // AI translation (DEC-0104) — English <-> Sinhala only.
  async translate(
    resourceId: string,
    body: TranslateResourceRequest,
  ): Promise<ResourceTranslationJobDTO> {
    const res = await apiRequest<TranslateResourceResponse>(`/resources/${resourceId}/translate`, {
      method: 'POST',
      body,
    });
    return res.job;
  },

  async getTranslationJob(jobId: string, signal?: AbortSignal): Promise<ResourceTranslationJobDTO> {
    const res = await apiRequest<GetResourceTranslationJobResponse>(
      `/resources/translation-jobs/${jobId}`,
      signal ? { signal } : {},
    );
    return res.job;
  },

  async approve(resourceId: string): Promise<ResourceDTO> {
    const res = await apiRequest<ApproveResourceResponse>(`/resources/${resourceId}/approve`, {
      method: 'POST',
    });
    return res.resource;
  },

  reject(resourceId: string): Promise<void> {
    return apiRequest<void>(`/resources/${resourceId}/reject`, { method: 'POST' });
  },

  // AI customization (per-resource, prompt-driven) — replaces whole-group
  // synthesis: a coach picks ONE resource and describes what to customize.
  async customize(
    resourceId: string,
    body: CustomizeResourceRequest,
  ): Promise<ResourceCustomizationJobDTO> {
    const res = await apiRequest<CustomizeResourceResponse>(`/resources/${resourceId}/customize`, {
      method: 'POST',
      body,
    });
    return res.job;
  },

  async getCustomizationJob(
    jobId: string,
    signal?: AbortSignal,
  ): Promise<ResourceCustomizationJobDTO> {
    const res = await apiRequest<GetResourceCustomizationJobResponse>(
      `/resources/customization-jobs/${jobId}`,
      signal ? { signal } : {},
    );
    return res.job;
  },

  /** Most recent customization job for a resource (any status), or `null`
   * if it's never been customized — resumes a coach's instruction into the
   * Customize modal after a failed attempt, even across a page reload. */
  async getLatestCustomizationJob(
    resourceId: string,
    signal?: AbortSignal,
  ): Promise<ResourceCustomizationJobDTO | null> {
    const res = await apiRequest<GetLatestResourceCustomizationJobResponse>(
      `/resources/${resourceId}/customization-jobs/latest`,
      signal ? { signal } : {},
    );
    return res.job;
  },

  // Version history (DEC-0154 follow-up) — many customizations of the same
  // source resource, exactly one active at a time.
  async listVersions(resourceId: string, signal?: AbortSignal): Promise<ResourceDTO[]> {
    const res = await apiRequest<ListResourceVersionsResponse>(
      `/resources/${resourceId}/versions`,
      signal ? { signal } : {},
    );
    return res.versions;
  },

  async activateVersion(resourceId: string): Promise<ResourceDTO> {
    const res = await apiRequest<ActivateResourceVersionResponse>(
      `/resources/${resourceId}/activate-version`,
      { method: 'POST' },
    );
    return res.resource;
  },

  /** Saves which of a group's current members are included in a future
   * "send group to client" (the group's settings dialog). */
  async setGroupSendSelection(
    groupId: string,
    body: SetGroupSendSelectionRequest,
  ): Promise<ResourceDTO[]> {
    const res = await apiRequest<SetGroupSendSelectionResponse>(
      `/resources/groups/${groupId}/send-selection`,
      { method: 'PUT', body },
    );
    return res.resources;
  },
};

// ── Resource Groups (folder-like grouping) ────────────────────────────

export const resourceGroupsApi = {
  async listAll(signal?: AbortSignal): Promise<ResourceGroupDTO[]> {
    const res = await apiRequest<ListResourceGroupsResponse>(
      '/resource-groups',
      signal ? { signal } : {},
    );
    return res.groups;
  },

  async create(body: CreateResourceGroupRequest): Promise<ResourceGroupDTO> {
    const res = await apiRequest<ResourceGroupResponse>('/resource-groups', {
      method: 'POST',
      body,
    });
    return res.group;
  },

  async update(id: string, body: UpdateResourceGroupRequest): Promise<ResourceGroupDTO> {
    const res = await apiRequest<ResourceGroupResponse>(`/resource-groups/${id}`, {
      method: 'PATCH',
      body,
    });
    return res.group;
  },

  remove(id: string): Promise<void> {
    return apiRequest<void>(`/resource-groups/${id}`, { method: 'DELETE' });
  },

  /** Sends every currently-sendable (non-`PENDING_REVIEW`) member of the
   * group to `clientId` in one action. */
  async sendGroup(
    clientId: string,
    groupId: string,
    body: SendResourceGroupRequest,
  ): Promise<SendResourceGroupResponse['deliveries']> {
    const res = await apiRequest<SendResourceGroupResponse>(
      `/clients/${clientId}/resource-groups/${groupId}/send`,
      { method: 'POST', body },
    );
    return res.deliveries;
  },
};

// ── Payments (bank-transfer submission review queue, P21-T05) ───────

export const paymentsApi = {
  list(query: ListPurchasesQuery = {}, signal?: AbortSignal): Promise<Paginated<PurchaseDTO>> {
    return apiRequest<Paginated<PurchaseDTO>>('/coach/payments', {
      query: {
        status: query.status,
        clientId: query.clientId,
        page: query.page,
        pageSize: query.pageSize,
      },
      ...(signal ? { signal } : {}),
    });
  },

  async get(id: string, signal?: AbortSignal): Promise<PurchaseDTO> {
    const res = await apiRequest<{ purchase: PurchaseDTO }>(
      `/coach/payments/${id}`,
      signal ? { signal } : {},
    );
    return res.purchase;
  },

  async listSlips(id: string, signal?: AbortSignal): Promise<ListPaymentSlipsResponse['slips']> {
    const res = await apiRequest<ListPaymentSlipsResponse>(
      `/coach/payments/${id}/slips`,
      signal ? { signal } : {},
    );
    return res.slips;
  },

  async verify(id: string): Promise<PurchaseDTO> {
    const res = await apiRequest<{ purchase: PurchaseDTO }>(`/coach/payments/${id}/verify`, {
      method: 'POST',
    });
    return res.purchase;
  },

  async reject(id: string, body: RejectPurchaseRequest): Promise<PurchaseDTO> {
    const res = await apiRequest<{ purchase: PurchaseDTO }>(`/coach/payments/${id}/reject`, {
      method: 'POST',
      body,
    });
    return res.purchase;
  },

  /**
   * A payment slip's raw bytes — deliberately NOT `apiRequest` (which
   * always parses JSON): the dual-audience `/api/v1/payments/*` route
   * (see services/api's purchase.routes.ts) streams the file directly, no
   * public signed URL, so the coach portal fetches it as a blob (with the
   * bearer token attached) and renders it via an object URL.
   */
  async getSlipBlob(purchaseId: string, slipId: string): Promise<Blob> {
    const url = `${API_BASE_URL}${API_PREFIX}/payments/${purchaseId}/slip/${slipId}`;
    const token = tokenStore.getAccessToken();
    const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) {
      throw new ApiError(res.status, 'SLIP_DOWNLOAD_FAILED', 'Could not load the payment slip.');
    }
    return res.blob();
  },
};

// ── Fingerprint Images (raw per-finger photos) ───────────────────────
// A deliberately separate module from fingerprintAnalysisApi above (that's
// the unrelated xlsx-derived Brain Skills Analysis) — one image per finger,
// coach upload-on-behalf of a client, replace-in-place on re-upload.

export const fingerprintImageApi = {
  async listForClient(clientId: string, signal?: AbortSignal): Promise<FingerprintImageDTO[]> {
    const res = await apiRequest<ListFingerprintImagesResponse>(
      `/clients/${clientId}/fingerprint-images`,
      signal ? { signal } : {},
    );
    return res.images;
  },

  async upload(
    clientId: string,
    finger: FingerImagePosition,
    file: File,
  ): Promise<FingerprintImageDTO> {
    const formData = new FormData();
    formData.append('file', file);
    const res = await apiRequest<UploadFingerprintImageResponse>(
      `/clients/${clientId}/fingerprint-images/${finger}`,
      { method: 'POST', body: formData },
    );
    return res.image;
  },

  remove(clientId: string, finger: FingerImagePosition): Promise<void> {
    return apiRequest<void>(`/clients/${clientId}/fingerprint-images/${finger}`, {
      method: 'DELETE',
    });
  },

  /**
   * A fingerprint image's raw bytes — deliberately NOT `apiRequest` (which
   * always parses JSON): the dual-audience `/api/v1/fingerprint-images/*`
   * route streams the file directly, no public signed URL (mirrors
   * `paymentsApi.getSlipBlob` — a biometric photo warrants a live session).
   */
  async getImageBlob(clientId: string, finger: FingerImagePosition): Promise<Blob> {
    const url = `${API_BASE_URL}${API_PREFIX}/fingerprint-images/${clientId}/${finger}`;
    const token = tokenStore.getAccessToken();
    const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) {
      throw new ApiError(
        res.status,
        'FINGERPRINT_IMAGE_DOWNLOAD_FAILED',
        'Could not load the fingerprint image.',
      );
    }
    return res.blob();
  },
};
